<template>
  <div v-if="user" class="user-info">
    <h1>
      <i class="iconfont icon-arrow_a" @click="router.back()"></i>
      {{ user.name }}
    </h1>
    <el-descriptions label-width="150px" border>
      <el-descriptions-item :label="$t('UIV.company')">
        {{ user.company?.trim() || $t("UIV.empty") }}
      </el-descriptions-item>
      <el-descriptions-item :label="$t('UIV.person')">
        {{ user.person?.trim() || $t("UIV.empty") }}
      </el-descriptions-item>
      <el-descriptions-item :label="$t('UIV.phone')">
        {{ user.phone?.trim() || $t("UIV.empty") }}
      </el-descriptions-item>
      <el-descriptions-item :label="$t('UIV.email')">
        {{ user.email?.trim() || $t("UIV.empty") }}
      </el-descriptions-item>
      <el-descriptions-item :label="$t('UIV.address')">
        {{ user.address?.trim() || $t("UIV.empty") }}
      </el-descriptions-item>
      <el-descriptions-item :label="$t('UIV.description')">
        {{ user.description?.trim() || $t("UIV.empty") }}
      </el-descriptions-item>
    </el-descriptions>
    <h1 style="margin-top: 20px">
      船舶列表
      <el-button type="primary" @click="createdAreaDialogVisible = true">
        创建区域
      </el-button>
    </h1>
    <div v-if="areaNameList.length > 0" class="area-ship-container">
      <el-scrollbar class="area-selection-box">
        <div
          v-for="area in areaNameList"
          :key="area.id"
          class="area"
          :class="{ selected: selectedAreaId == area.id }"
          @click="handleAreaClick(area.id)"
        >
          {{ area.name }}
        </div>
      </el-scrollbar>
      <el-scrollbar v-if="shipList.length > 0" class="ship-list">
        <div v-for="ship in shipList" :key="ship.id" class="ship">
          <p style="width: 200px">{{ ship.ship.name }}</p>
          <p style="width: 200px">
            <i
              class="iconfont icon-dot"
              :style="{ color: getStatusColor(ship.status.id) }"
            ></i>
            {{ getName(ship.status) }}
          </p>
          <div
            v-if="ship.authorization != null && ship.authorization.status !== 0"
            style="width: 150px; font-size: 10px"
            :style="{
              marginLeft: ship.authorization.status != 0 ? 'auto' : '0',
            }"
          >
            <p>{{ $d(new Date(ship.authorization.startTime), "long") }}</p>
            <p>{{ $d(new Date(ship.authorization.endTime), "long") }}</p>
          </div>
          <p
            style="width: 100px"
            :style="{
              marginLeft:
                ship.authorization == null || ship.authorization.status == 0
                  ? 'auto'
                  : '0',
            }"
            class="authorization-status-text"
            :class="getAuthorizationStatusClass(ship.authorization)"
          >
            {{ getAuthorizationStatus(ship.authorization) }}
          </p>
          <p style="width: 200px">
            <el-button
              size="small"
              :type="
                ship.authorization == null || ship.authorization!.status == 0
                  ? 'info'
                  : 'success'
              "
              :disabled="
                ship.authorization == null || ship.authorization.status != 1
              "
              @click="handleCheckInfoClick(ship.id)"
            >
              {{ $t("UIV.checkInfo") }}
            </el-button>
          </p>
        </div>
      </el-scrollbar>
      <div v-else class="empty-ship-list">
        {{ $t("ship.noShip") }}
      </div>
    </div>
    <div v-else class="area-ship-container" style="height: 60px">
      {{ $t("UIV.empty") }}
    </div>
    <el-dialog v-model="createdAreaDialogVisible" title="创建区域" width="400">
      <el-input v-model="createdAreaName" placeholder="输入区域名称"></el-input>
      <el-button
        type="primary"
        style="width: 100%; margin-top: 7px"
        @click="submitCreatedArea"
      >
        创建
      </el-button>
    </el-dialog>
  </div>
</template>
<script lang="ts" setup>
import {
  createAreaApi,
  getAreaNameListApi,
  getShipWithTypeListByAreaId,
} from "@/api/productApi";
import { fetchUserByIdApi } from "@/api/user/userApi";
import router from "@/router";
import { Area, ShipAuthorization, ShipVO } from "@/types/product";
import { User } from "@/types/user";
import { orderBy } from "lodash-es";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { getName } from "@/util/i18nUtil";
import { getStatusColor } from "@/util/colorUtil";
import { useI18n } from "vue-i18n";
import { ElMessage } from "element-plus";
import _ from "lodash";

const route = useRoute();
const userId = computed(() => route.query.userId as string);
const user = ref<User>();
const { t } = useI18n();

// 获取用户基本信息
const fetchUserById = async () => {
  if (userId.value) {
    const response = await fetchUserByIdApi(userId.value);
    if (response) {
      user.value = response;
    } else {
      sessionStorage.setItem(
        "previousRoute",
        (router.options.history.state.back as string) || "/"
      );
      router.push({ name: "contentError" });
    }
  } else {
    console.log(router.options.history.state.back as string);
    sessionStorage.setItem(
      "previousRoute",
      (router.options.history.state.back as string) || "/"
    );
    router.push({ name: "contentError" });
  }
};

// 获取区域列表
const areaNameList = ref<Area[]>([]);
const fetchAreaList = async () => {
  const response = await getAreaNameListApi(userId.value);
  if (response) {
    areaNameList.value = response;
    if (response[0]) {
      selectedAreaId.value = response[0].id;
      fetchShipList(response[0].id);
    }
  }
};

// 获取船舶列表
const shipList = ref<ShipVO[]>([]);
const fetchShipList = async (areaId: string) => {
  const response = await getShipWithTypeListByAreaId(areaId);
  if (response) {
    shipList.value = orderBy(response, ["status.id"], ["desc"]);
  }
};

// 获取授权状态
const getAuthorizationStatus = (
  authorization: ShipAuthorization | undefined
) => {
  if (!authorization) return t("UIV.unauthorized");
  if (authorization.status == 0) return t("UIV.unauthorized");
  else if (authorization.status == 1) return t("UIV.authorized");
  else return t("UIV.authorizing");
};
const getAuthorizationStatusClass = (
  authorization: ShipAuthorization | undefined
) => {
  if (!authorization) return "text-red";
  if (authorization.status == 0) return "text-red";
  else if (authorization.status == 1) return "text-green";
  else return "text-yellow";
};

// 处理area点击事件
const selectedAreaId = ref<string>();
const handleAreaClick = (areaId: string) => {
  selectedAreaId.value = areaId;
  fetchShipList(areaId);
};
// 查看详情按钮点击
const handleCheckInfoClick = (shipId: string) => {
  router.push({
    name: "shipInfo",
    query: { shipId: shipId },
  });
};

// 创建区域
const createdAreaDialogVisible = ref<boolean>(false);
const createdAreaName = ref<string>("");
const btnCreatedAreaLoading = ref<boolean>(false);
const submitCreatedArea = async () => {
  if (createdAreaName.value.trim() === "") {
    ElMessage.error("区域名称不能为空！");
  }
  if (_.some(areaNameList.value, { name: createdAreaName.value })) {
    ElMessage.error("区域名称已存在！");
    return;
  }
  btnCreatedAreaLoading.value = true;
  const response = await createAreaApi(userId.value, createdAreaName.value);
  btnCreatedAreaLoading.value = false;
  console.log(response);
  if (response === true) {
    createdAreaName.value = "";
    ElMessage.success("创建成功！");
    createdAreaName.value = "";
    fetchAreaList();
    createdAreaDialogVisible.value = false;
  } else {
    ElMessage.error("创建失败！");
  }
};

onMounted(() => {
  fetchUserById();
  fetchAreaList();
});
</script>
<style lang="less" scoped>
.user-info {
  min-width: 1200px !important;
  padding: 7px;
}
.icon-arrow_a {
  font-size: 26px;
  cursor: pointer;
}
.area-ship-container {
  display: flex;
  width: 100%;
  height: 500px;
  position: relative;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  border: 1px solid #d6d6d6;
  padding: 20px;
  overflow: hidden;
}
.area-selection-box {
  width: 200px;
  height: 100%;
  border-right: 1px solid #dbdbdb;
  // box-shadow: 1px 0 5px 1px rgb(226, 226, 226);
}
.ship-list {
  width: calc(100% - 200px);
  height: 100%;
  position: relative;
  top: -10px;
}
.empty-ship-list {
  width: calc(100% - 200px);
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2em;
  background-color: rgb(228, 228, 228);
}
.area {
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-family: "SimHei";
  font-weight: bolder;
  letter-spacing: 1px;
  border-bottom: 1px solid #cfcfcf;
  background-color: #ffd4c0;
  color: rgb(41, 73, 83);
  text-shadow: 0 0 1px rgb(150, 215, 236);
  cursor: pointer;
  transition: filter 0.3s ease;
  &:hover {
    filter: brightness(90%);
  }
}
.selected {
  filter: brightness(80%);
  &:hover {
    filter: brightness(70%);
  }
}
.ship {
  width: calc(100% - 20px);
  height: 40px;
  margin: 10px;
  box-shadow: 0 0 5px 1px rgb(221, 221, 221);
  display: flex;
  align-items: center;
  padding: 4px 20px;
}
</style>
